import { useState } from "react";
import { BookOpen, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import Pagination from "@/components/shared/Pagination";
import { money } from "./ledgerDisplay";

// Owed means at least a cent: floating-point leftovers like 2e-13 show as $0.00.
const OWED = 0.005;

// Everything that differs between the two lists, in one place.
const KIND = {
  reseller: {
    title: "Resellers",
    noun: "reseller",
    idOf: (r) => r.store_id,
    isPending: (r) => (r.pending_payout || 0) > OWED,
    searchText: (r) => `${r.business_name || ""} ${r.subdomain || ""}`,
  },
  distributor: {
    title: "Distributors",
    noun: "distributor",
    idOf: (r) => r.distributor_id,
    isPending: (r) => (r.pending_earnings || 0) > OWED,
    searchText: (r) => `${r.name || ""} ${r.email || ""}`,
  },
};

const SUB_TRIGGER = "px-4 py-1.5 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm";

/**
 * The reseller / distributor list on the admin Payouts page: "Pending" (someone is owed
 * money) and "All" as two views of ONE table, a search box, client-side paging, and a
 * Ledger button per row that opens that owner's full ledger page.
 */
export default function OwnerLedgerList({ kind, rows, sub, onSubChange, onOpenLedger }) {
  const cfg = KIND[kind];
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const pending = rows.filter(cfg.isPending);
  const source = sub === "pending" ? pending : rows;
  const needle = search.trim().toLowerCase();
  const filtered = needle ? source.filter((r) => cfg.searchText(r).toLowerCase().includes(needle)) : source;

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const current = Math.min(page, totalPages); // a refetch can shrink the list under the page you're on
  const visible = filtered.slice((current - 1) * pageSize, current * pageSize);

  const changeSub = (next) => { onSubChange(next); setPage(1); };
  const colSpan = kind === "reseller" ? 6 : 7;

  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden" data-testid={`owner-list-${kind}`}>
      <div className="p-4 border-b flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold text-slate-900">{cfg.title}</h3>
          <p className="text-sm text-slate-500">Open a {cfg.noun}'s full ledger — credits and debits</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Tabs value={sub} onValueChange={changeSub}>
            <TabsList className="bg-slate-100 p-1">
              <TabsTrigger value="pending" className={SUB_TRIGGER}>Pending ({pending.length})</TabsTrigger>
              <TabsTrigger value="all" className={SUB_TRIGGER}>All ({rows.length})</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder={`Search ${cfg.noun}s`}
              className="pl-8 h-9 w-56 bg-white"
              data-testid={`owner-search-${kind}`}
            />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            {kind === "reseller" ? (
              <TableRow className="bg-slate-50">
                <TableHead>Reseller</TableHead>
                <TableHead className="text-right">Pending Payout</TableHead>
                <TableHead className="text-right">Total Earnings</TableHead>
                <TableHead className="text-center">Orders</TableHead>
                <TableHead className="text-right">Total Sales</TableHead>
                <TableHead className="text-right">Ledger</TableHead>
              </TableRow>
            ) : (
              <TableRow className="bg-slate-50">
                <TableHead>Distributor</TableHead>
                <TableHead className="text-center">Commission %</TableHead>
                <TableHead className="text-center">Resellers</TableHead>
                <TableHead className="text-right">Total Earned</TableHead>
                <TableHead className="text-right">Pending</TableHead>
                <TableHead className="text-right">In Payout</TableHead>
                <TableHead className="text-right">Ledger</TableHead>
              </TableRow>
            )}
          </TableHeader>
          <TableBody>
            {visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} className="text-center py-10 text-slate-500" data-testid={`owner-empty-${kind}`}>
                  {needle
                    ? `No ${cfg.noun} matches "${search.trim()}"`
                    : sub === "pending"
                      ? `No ${cfg.noun} has a pending balance right now`
                      : `No ${cfg.noun}s found`}
                </TableCell>
              </TableRow>
            ) : (
              visible.map((r) => (
                <TableRow key={cfg.idOf(r)}>
                  {kind === "reseller" ? (
                    <>
                      <TableCell>
                        <p className="font-medium text-slate-900">{r.business_name}</p>
                        <p className="text-xs text-slate-500">{r.subdomain}</p>
                      </TableCell>
                      <TableCell className="text-right font-bold text-amber-600">{money(r.pending_payout)}</TableCell>
                      <TableCell className="text-right">{money(r.total_earnings)}</TableCell>
                      <TableCell className="text-center">{r.order_count || 0}</TableCell>
                      <TableCell className="text-right">{money(r.total_sales)}</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>
                        <p className="font-medium text-slate-900">{r.name}</p>
                        <p className="text-xs text-slate-500">{r.email}</p>
                      </TableCell>
                      <TableCell className="text-center"><Badge variant="secondary">{r.commission_percent}%</Badge></TableCell>
                      <TableCell className="text-center">{r.assigned_resellers_count || 0}</TableCell>
                      <TableCell className="text-right font-medium">{money(r.total_earnings)}</TableCell>
                      <TableCell className="text-right font-medium text-amber-600">{money(r.pending_earnings)}</TableCell>
                      <TableCell className="text-right">
                        {(r.in_payout || 0) > 0
                          ? <span className="font-medium text-blue-600">{money(r.in_payout)}</span>
                          : <span className="text-slate-400">-</span>}
                      </TableCell>
                    </>
                  )}
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onOpenLedger(kind, cfg.idOf(r))}
                      data-testid={`open-ledger-${cfg.idOf(r)}`}
                    >
                      <BookOpen className="w-4 h-4 mr-1" />
                      Ledger
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {filtered.length > 0 && (
        <div className="p-4 border-t">
          <Pagination
            page={current}
            totalPages={totalPages}
            onChange={setPage}
            totalItems={filtered.length}
            pageSize={pageSize}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        </div>
      )}
    </div>
  );
}
